<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button
                variant="link"
                class="list-view-icon-btn"
                :class="{ 'list-view-icon-btn--active': filtersOpen }"
                v-b-tooltip.hover.bottom
                :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter stock-control-filter-search">
              <label class="filter-label">Producto</label>
              <b-form-input
                v-model="filters.search"
                size="sm"
                placeholder="Código, código de barras o nombre"
                @keyup.enter.native="applyFilters()" />
            </div>
            <div class="list-view-filter" style="width: 170px;">
              <label class="filter-label">Control de stock</label>
              <b-form-select v-model="filters.control_stock" :options="controlStockOptions" size="sm" @change="applyFilters()" />
            </div>
            <b-button
              variant="outline-dark"
              size="sm"
              class="stock-control-filter-btn"
              v-b-tooltip.hover
              title="Aplicar filtros"
              @click="applyFilters()">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <div class="stock-control-workspace">
        <aside v-if="showContextPanel" class="stock-control-context-panel">
          <b-card no-body class="stock-control-tree-card">
            <div class="stock-control-panel-title">
              <span><AppIcon name="account_tree" /> Catálogo</span>
              <b-button
                variant="link"
                class="stock-control-panel-toggle"
                v-b-tooltip.hover
                title="Ocultar catálogo"
                @click="showContextPanel = false">
                <AppIcon name="chevron-left" />
              </b-button>
            </div>

            <div class="stock-control-tree">
              <div v-if="tree.loading" class="stock-control-loader">
                <b-spinner small />
                <span>Cargando categorías...</span>
              </div>

              <EmptyState
                v-else-if="!tree.categories.length"
                icon="category"
                title="Sin categorías"
                description="No hay categorías de productos para mostrar." />

              <div v-else>
                <div v-for="category in tree.categories" :key="'cat-' + category.id" class="stock-control-node">
                  <div
                    class="stock-control-node__row"
                    :class="{ 'stock-control-node__row--active': isSelected('category', category.id) }">
                    <b-button variant="link" class="stock-control-node__expand" @click="toggleCategory(category)">
                      <AppIcon :name="category.expanded ? 'chevron-down' : 'chevron-right'" />
                    </b-button>
                    <button type="button" class="stock-control-node__label" @click="selectCategory(category)">
                      <span class="stock-control-node__name">{{ category.name }}</span>
                      <span class="stock-control-node__meta">{{ category.subcategorys_count || 0 }} subcategorías</span>
                    </button>
                    <div class="stock-control-node__actions">
                      <b-button
                        variant="link"
                        class="stock-control-node__action"
                        :disabled="category.saving"
                        v-b-tooltip.hover.left
                        title="Toda la categoría controla stock"
                        @click.stop="bulkControlStock('category', category, true)">
                        <AppIcon name="check" />
                      </b-button>
                      <b-button
                        variant="link"
                        class="stock-control-node__action"
                        :disabled="category.saving"
                        v-b-tooltip.hover.right
                        title="Toda la categoría no controla stock"
                        @click.stop="bulkControlStock('category', category, false)">
                        <AppIcon name="x" />
                      </b-button>
                    </div>
                  </div>

                  <div v-if="category.expanded" class="stock-control-children">
                    <div v-if="category.loading" class="stock-control-loader stock-control-loader--inline">
                      <b-spinner small />
                      <span>Cargando subcategorías...</span>
                    </div>
                    <div v-else-if="!category.subcategories.length" class="stock-control-empty-inline">
                      Sin subcategorías
                    </div>
                    <template v-else>
                      <div
                        v-for="subcategory in category.subcategories"
                        :key="'sub-' + subcategory.id"
                        class="stock-control-node stock-control-node--sub">
                        <div
                          class="stock-control-node__row stock-control-node__row--sub"
                          :class="{ 'stock-control-node__row--active': isSelected('subcategory', subcategory.id) }">
                          <AppIcon name="subdirectory-arrow-right" class="stock-control-sub-icon" />
                          <button type="button" class="stock-control-node__label" @click="selectSubcategory(category, subcategory)">
                            <span class="stock-control-node__name">{{ getSubcategoryName(subcategory) }}</span>
                            <span class="stock-control-node__meta">{{ subcategory.products_count || 0 }} productos</span>
                          </button>
                          <div class="stock-control-node__actions">
                            <b-button
                              variant="link"
                              class="stock-control-node__action"
                              :disabled="subcategory.saving"
                              v-b-tooltip.hover.left
                              title="Toda la subcategoría controla stock"
                              @click.stop="bulkControlStock('subcategory', subcategory, true)">
                              <AppIcon name="check" />
                            </b-button>
                            <b-button
                              variant="link"
                              class="stock-control-node__action"
                              :disabled="subcategory.saving"
                              v-b-tooltip.hover.right
                              title="Toda la subcategoría no controla stock"
                              @click.stop="bulkControlStock('subcategory', subcategory, false)">
                              <AppIcon name="x" />
                            </b-button>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </b-card>
        </aside>

        <div
          v-else
          class="stock-control-context-collapsed"
          v-b-tooltip.hover.right
          title="Mostrar catálogo"
          @click="showContextPanel = true">
          <AppIcon name="chevron-right" />
          <span>Catálogo</span>
        </div>

        <main class="stock-control-results" :class="{ 'stock-control-results--expanded': !showContextPanel }">
          <b-card no-body>
            <div class="stock-control-results-header">
              <div>
                <div class="stock-control-results-title" v-if="selectedNode && selectedNode.path">
                  <template v-for="(part, index) in selectedNode.path">
                    <AppIcon v-if="index > 0" :key="'path-icon-' + index" name="chevron-right" class="stock-control-results-path-icon" />
                    <strong :key="'path-part-' + index">{{ part }}</strong>
                  </template>
                </div>
                <div class="stock-control-results-title" v-else>{{ resultsTitle }}</div>
                <div class="stock-control-results-subtitle" v-if="!selectedNode">{{ resultsSubtitle }}</div>
              </div>
              <div class="stock-control-results-summary" v-if="table.tablePaginate">
                <b-badge variant="light" class="stock-control-results-count">
                  {{ table.tablePaginate.total }} productos
                </b-badge>
                <span class="stock-control-results-page">
                  Página {{ table.tablePaginate.current_page }} de {{ table.tablePaginate.last_page }}
                </span>
              </div>
            </div>

            <b-table
              v-if="table.items.length || table.isBusy"
              class="app-table mb-0"
              responsive
              sticky-header="60vh"
              hover
              small
              :items="table.items"
              :fields="table.fields"
              :busy="table.isBusy">
              <template v-slot:table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>Cargando...</strong>
                </div>
              </template>

              <template v-slot:cell(code)="data">
                <div class="stock-control-product-code">{{ data.item.code }}</div>
                <div v-if="data.item.barcode && data.item.barcode != data.item.code" class="stock-control-product-barcode">{{ data.item.barcode }}</div>
              </template>

              <template v-slot:cell(name)="data">
                <div class="stock-control-product-name">{{ data.item.name }}</div>
              </template>

              <template v-slot:cell(control_stock)="data">
                <b-form-checkbox
                  switch
                  :checked="data.item.control_stock !== false"
                  :disabled="data.item._savingControlStock"
                  @change="toggleControlStock(data.item, $event)">
                  {{ data.item.control_stock === false ? 'No controla' : 'Controla' }}
                </b-form-checkbox>
              </template>
            </b-table>

            <EmptyState
              v-else
              icon="inventory_2"
              :title="emptyTitle"
              :description="emptyDescription" />

            <div class="app-table-footer stock-control-table-footer">
              <nav v-if="table.tablePaginate && table.tablePaginate.total > table.tablePaginate.per_page">
                <b-pagination
                  class="mb-0"
                  size="sm"
                  pills
                  :total-rows="table.tablePaginate.total"
                  :per-page="table.tablePaginate.per_page"
                  v-model="table.currentPage"
                  @input="loadProducts()" />
              </nav>
            </div>
          </b-card>
        </main>
      </div>

      <b-row>
        <b-col lg="12">
          <b-card body-class="py-2">
            <div class="list-view-footer">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                <AppIcon name="back" />
                Volver
              </b-button>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default {
    components: {
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.DEPOSITOS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crudDepositsStockControl',
          elements: {}
        },
        filtersOpen: false,
        showContextPanel: true,
        tree: {
          loading: false,
          categories: [],
        },
        selectedNode: null,
        table: {
          items: [],
          fields: [],
          currentPage: 1,
          isBusy: false,
          tablePaginate: null,
        },
        filters: {
          search: '',
          control_stock: 'all',
        },
        controlStockOptions: [
          { value: 'all', text: 'Todos' },
          { value: '1', text: 'Controla' },
          { value: '0', text: 'No controla' },
        ],
      }
    },
    computed: {
      resultsTitle() {
        if(this.filters.search) {
          return 'Resultados de búsqueda'
        }
        return 'Productos'
      },
      resultsSubtitle() {
        if(this.selectedNode && this.selectedNode.path) {
          return this.selectedNode.path
        }
        if(this.filters.search) {
          return 'Filtrado por código, código de barras o nombre'
        }
        return 'Seleccioná una categoría o subcategoría para cargar productos'
      },
      emptyTitle() {
        if(!this.selectedNode && !this.filters.search) {
          return 'Seleccioná un punto del catálogo'
        }
        return 'Sin productos'
      },
      emptyDescription() {
        if(!this.selectedNode && !this.filters.search) {
          return 'Abrí una categoría o subcategoría para cargar sus productos de forma paginada.'
        }
        return 'No hay productos para los filtros aplicados.'
      },
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_crud_deposits_stock_control', value ? 'true' : 'false')
      },
      showContextPanel(value) {
        StorageLocal.setValue('crud_deposits_stock_control_context_panel_open', value ? 'true' : 'false')
      },
    },
    created() {
      Helper.hasAccessView(this.access)
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_deposits_stock_control') === 'true'
      if(StorageLocal.getValue('crud_deposits_stock_control_context_panel_open') !== null) {
        this.showContextPanel = StorageLocal.getValue('crud_deposits_stock_control_context_panel_open') === 'true'
      }
    },
    mounted() {
      this.loadFieldTable()
      this.loadCategories()
    },
    methods: {
      loadFieldTable() {
        this.table.fields = []
        this.table.fields.push({key: 'code', label: 'Código / barras', class: 'align-middle', width: '150px'})
        this.table.fields.push({key: 'name', label: 'Producto', class: 'align-middle'})
        this.table.fields.push({key: 'control_stock', label: 'Control de stock', class: 'align-middle text-center', width: '210px'})
      },
      loadCategories() {
        this.tree.loading = true
        serviceAPI.obtenerCategoriasStockControl(this.getTreeFilterParams())
          .then(response => {
            this.tree.categories = response.data.map(item => {
              return Object.assign({}, item, {
                expanded: false,
                loading: false,
                loaded: false,
                saving: false,
                subcategories: [],
              })
            })
            this.syncSelectedNodeWithTree()
            this.tree.loading = false
          })
          .catch(error => {
            this.tree.loading = false
            this.$awn.alert(Error.showError(error))
          })
      },
      toggleCategory(category) {
        category.expanded = !category.expanded
        if(category.expanded && !category.loaded) {
          this.loadSubcategories(category)
        }
      },
      loadSubcategories(category) {
        this.$set(category, 'loading', true)
        serviceAPI.obtenerSubcategoriasStockControl(Object.assign({category_id: category.id}, this.getTreeFilterParams()))
          .then(response => {
            this.$set(category, 'subcategories', response.data.map(item => {
              return Object.assign({}, item, { saving: false })
            }))
            this.$set(category, 'loaded', true)
            this.$set(category, 'loading', false)
          })
          .catch(error => {
            this.$set(category, 'loading', false)
            this.$awn.alert(Error.showError(error))
          })
      },
      selectCategory(category) {
        this.selectedNode = { type: 'category', id: category.id, name: category.name, path: [category.name] }
        this.table.currentPage = 1
        this.loadProducts()
      },
      selectSubcategory(category, subcategory) {
        this.selectedNode = {
          type: 'subcategory',
          id: subcategory.id,
          name: this.getSubcategoryName(subcategory),
          category_id: category.id,
          path: [category.name, this.getSubcategoryName(subcategory)],
        }
        this.table.currentPage = 1
        this.loadProducts()
      },
      isSelected(type, id) {
        return this.selectedNode && this.selectedNode.type === type && this.selectedNode.id === id
      },
      applyFilters() {
        this.table.currentPage = 1
        this.loadCategories()
        if(!this.selectedNode && !this.filters.search) {
          this.table.items = []
          this.table.tablePaginate = null
          return
        }
        this.loadProducts()
      },
      getTreeFilterParams() {
        return {
          search: this.filters.search,
          control_stock: this.filters.control_stock,
        }
      },
      syncSelectedNodeWithTree() {
        if(!this.selectedNode) {
          return
        }

        var categoryExists = this.tree.categories.some(category => category.id === this.selectedNode.id)
        if(this.selectedNode.type === 'category' && !categoryExists) {
          this.selectedNode = null
          this.table.items = []
          this.table.tablePaginate = null
        }
      },
      loadProducts() {
        if(!this.selectedNode && !this.filters.search) {
          return
        }

        var params = {
          search: this.filters.search,
          control_stock: this.filters.control_stock,
        }

        if(this.selectedNode && this.selectedNode.type === 'category') {
          params.category_id = this.selectedNode.id
        }
        if(this.selectedNode && this.selectedNode.type === 'subcategory') {
          params.subcategory_id = this.selectedNode.id
        }

        this.table.isBusy = true
        serviceAPI.obtenerProductosStockControl(params, this.table.currentPage)
          .then(response => {
            this.table.tablePaginate = response.data
            this.table.items = response.data.data
            this.table.isBusy = false
          })
          .catch(error => {
            this.table.isBusy = false
            this.$awn.alert(Error.showError(error))
          })
      },
      toggleControlStock(item, value) {
        this.$set(item, '_savingControlStock', true)
        serviceAPI.editarControlStock({id: item.id, control_stock: value})
          .then(() => {
            this.$set(item, 'control_stock', value)
            this.$set(item, '_savingControlStock', false)
            this.$awn.success(value ? 'El producto controla stock' : 'El producto no controla stock')
          })
          .catch(error => {
            this.$set(item, '_savingControlStock', false)
            this.$awn.alert(Error.showError(error))
          })
      },
      bulkControlStock(scope, item, value) {
        var action = value ? 'van a controlar stock' : 'no van a controlar stock'
        var target = scope === 'category' ? 'categoría' : 'subcategoría'
        var filterText = this.getBulkFilterText()
        var message = 'Los productos de esta ' + target + filterText + ' ' + action + '.'

        this.$bvModal.msgBoxConfirm(message, {
          title: value ? 'Permitir control de stock' : 'Quitar control de stock',
          size: 'sm',
          okVariant: value ? 'primary' : 'danger',
          okTitle: value ? 'Permitir' : 'Quitar',
          cancelTitle: 'Cancelar',
          centered: true
        }).then(confirmed => {
          if(!confirmed) {
            return
          }

          this.$set(item, 'saving', true)
          var params = Object.assign({}, this.getTreeFilterParams(), {
            scope: scope,
            target_control_stock: value,
          })

          if(scope === 'category') {
            params.category_id = item.id
          }
          if(scope === 'subcategory') {
            params.subcategory_id = item.id
          }

          serviceAPI.editarControlStockMasivo(params)
            .then(response => {
              this.$set(item, 'saving', false)
              this.$awn.success((response.data.updated || 0) + ' productos actualizados')
              if(this.selectedNode) {
                this.loadProducts()
              }
            })
            .catch(error => {
              this.$set(item, 'saving', false)
              this.$awn.alert(Error.showError(error))
          })
        })
      },
      getBulkFilterText() {
        var filters = []

        if(this.filters.search) {
          filters.push('coincidentes con "' + this.filters.search + '"')
        }

        if(this.filters.control_stock === '1') {
          filters.push('que hoy controlan stock')
        }

        if(this.filters.control_stock === '0') {
          filters.push('que hoy no controlan stock')
        }

        if(!filters.length) {
          return ''
        }

        return ' filtrados (' + filters.join(', ') + ')'
      },
      getSubcategoryName(subcategory) {
        if(subcategory.parent) {
          return subcategory.parent.name + ' / ' + subcategory.name
        }
        return subcategory.name
      },
    }
  }
</script>

<style scoped>
  .stock-control-filter-search {
    min-width: 260px;
    flex: 1 1 320px;
  }

  .stock-control-filter-btn {
    height: 31px;
    width: 31px;
    padding: 0;
    flex-shrink: 0;
  }

  .stock-control-workspace {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .stock-control-context-panel {
    flex: 0 0 320px;
    max-width: 320px;
    margin-top: 8px;
  }

  .stock-control-results {
    flex: 1;
    min-width: 0;
    margin-top: 8px;
  }

  .stock-control-tree-card {
    overflow: hidden;
  }

  .stock-control-panel-title,
  .stock-control-results-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--app-color-border);
  }

  .stock-control-panel-title span,
  .stock-control-results-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--app-color-text);
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-semibold);
  }

  .stock-control-panel-toggle {
    width: 27px;
    height: 27px;
    padding: 0;
    color: var(--app-color-icon);
  }

  .stock-control-tree {
    max-height: 68vh;
    overflow: auto;
    padding: 8px;
  }

  .stock-control-node__row {
    display: flex;
    align-items: center;
    min-height: 42px;
    gap: 4px;
    border-radius: 7px;
    padding: 3px 4px;
  }

  .stock-control-node__row:hover,
  .stock-control-node__row--active {
    background: var(--app-color-primary-soft);
  }

  .stock-control-node__row--active {
    box-shadow: inset 3px 0 0 var(--app-color-primary);
  }

  .stock-control-node__row--sub {
    min-height: 38px;
  }

  .stock-control-node__expand,
  .stock-control-node__action {
    width: 27px;
    height: 27px;
    padding: 0;
    color: var(--app-color-icon);
    flex: 0 0 auto;
  }

  .stock-control-node__label {
    min-width: 0;
    flex: 1 1 auto;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    text-align: left;
  }

  .stock-control-node__name,
  .stock-control-node__meta {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .stock-control-node__name {
    color: var(--app-color-text);
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-medium);
  }

  .stock-control-node__meta,
  .stock-control-results-subtitle,
  .stock-control-product-barcode {
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-xs);
  }

  .stock-control-node__actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }

  .stock-control-children {
    margin-left: 22px;
    padding-left: 8px;
    border-left: 1px solid var(--app-color-border);
  }

  .stock-control-sub-icon {
    color: var(--app-color-icon);
    flex: 0 0 auto;
  }

  .stock-control-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px 10px;
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-sm);
  }

  .stock-control-loader--inline {
    justify-content: flex-start;
    padding: 10px 4px;
  }

  .stock-control-empty-inline {
    padding: 10px 4px;
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-xs);
  }

  .stock-control-results-header {
    background: var(--app-color-surface);
  }

  .stock-control-results-title {
    display: block;
  }

  .stock-control-results-subtitle {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  .stock-control-results-subtitle strong {
    color: var(--app-color-text);
    font-weight: var(--app-font-semibold);
  }

  .stock-control-results-path-icon {
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-xs);
  }

  .stock-control-results-count {
    border: 1px solid var(--app-color-border);
    color: var(--app-color-text-secondary);
    font-weight: var(--app-font-semibold);
  }

  .stock-control-results-summary {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
    text-align: right;
  }

  .stock-control-results-page {
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-xs);
    font-weight: var(--app-font-semibold);
  }

  .stock-control-product-code,
  .stock-control-product-name {
    color: var(--app-color-text);
    font-weight: var(--app-font-medium);
  }

  .stock-control-table-footer {
    padding-right: 12px;
    justify-content: flex-end;
  }

  .stock-control-context-collapsed {
    display: flex;
    flex: 0 0 34px;
    min-height: 260px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 8px;
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-primary);
    cursor: pointer;
    writing-mode: vertical-rl;
  }

  @media (max-width: 767.98px) {
    .stock-control-tree {
      max-height: none;
    }

    .stock-control-filter-search {
      min-width: 100%;
    }

    .stock-control-workspace {
      flex-direction: column;
    }

    .stock-control-context-panel,
    .stock-control-results,
    .stock-control-results--expanded {
      width: 100%;
      max-width: none;
      flex-basis: auto;
    }

    .stock-control-context-collapsed {
      width: 100%;
      min-height: 34px;
      flex-basis: auto;
      margin-top: 8px;
      writing-mode: horizontal-tb;
    }
  }
</style>
